import {
  getTaskProgressV1,
  possibleErrorMessageKeys,
} from "./getTaskProgress";

type Options = {
  useV1Polling?: true;
  tokenOverride?: string;
};

type SuccessfullTaskResult = {
  message: string;
  success: boolean;
};

interface SuccessfullTaskResponse<T> {
  state: "SUCCESS";
  result: T & SuccessfullTaskResult;
}

interface TaskInfo {
  task_id: string;
}

interface FailureTaskResponse {
  state: "FAILURE";
  result: string | any;
}

interface NextTaskResponse {
  state: "SUCCESS";
  result: { next_task: string } & { tasks_info: TaskInfo[] };
}

type TaskId = string;
type TaskResult<T> =
  | SuccessfullTaskResponse<T>
  | FailureTaskResponse
  | NextTaskResponse;

type TaskResponse<T> = Record<TaskId, TaskResult<T>>;

type TaskIdResponse1 = { task_id: string; task_url?: string };

type TaskIdResponse2 = { task: string };

type TaskIdResponse = TaskIdResponse1 | TaskIdResponse2;

export type Response<T> = {
  task_results: TaskResponse<T>;
};

const DEFAULT_POLLING_INTERVAL = 5000;

const isTaskResponse1 = (response: any): response is TaskIdResponse1 => {
  return Boolean(response.task_id);
};

const isTaskResponse2 = (response: any): response is TaskIdResponse2 => {
  return Boolean(response.task);
};

const isTaskResponse = (response: any): response is TaskIdResponse => {
  return isTaskResponse1(response) || isTaskResponse2(response);
};

const hasTaskFinished = <T>(
  taskResult: TaskResult<T>
): taskResult is SuccessfullTaskResponse<T> => {
  return taskResult.state === "SUCCESS";
};

const getNextTaskIds = <T>(successfulTask: TaskResult<T>) => {
  const taskResult = successfulTask.result as NextTaskResponse["result"];

  if (!taskResult) return undefined;
  if (taskResult.next_task) return [taskResult.next_task];

  const nextTaskIds = taskResult.tasks_info
    ?.filter((t) => t.task_id)
    ?.map((t) => t.task_id);

  return nextTaskIds ?? [];
};

const hasOperationFailed = <T>(
  taskResult: TaskResult<T>
): taskResult is SuccessfullTaskResponse<T> => {
  return taskResult.result.success === false;
};

const someTaskInfoHasErrorType = <T>(
  taskResult: TaskResult<T>
): taskResult is SuccessfullTaskResponse<T> => {
  return taskResult?.result?.tasks_info?.some?.(
    (task: TaskResult<T>["result"]) => task.error_type
  );
};

const isFailureTaskResult = <T>(
  taskResult: TaskResult<T>
): taskResult is FailureTaskResponse => {
  return taskResult.state === "FAILURE";
};

const delay = async (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * A wrapper for network requests that may start background tasks.
 * If the request starts a task, a `TaskIdResponse` is returned and polling starts
 * until the task finishes. Otherwise, the promise resolves with `response.data`.
 * @param request Function to be called.
 * @param args Array of arguments passed to the request function.
 * @param pollingIntervalMs
 * @param shouldRequestResourceAgainAfterTaskFinishes If true, the original request will fire again when polling finishes.
 * @param fallbackFailureMessage Optional message thrown to caller in case of a task error.
 * @param notifyHasTask Optional callback that fires if polling starts.
 * @param options
 * @param maxTimeoutMs Maximum time to wait for task completion (default: 60 seconds)
 */
export const pollTaskId = async <T, A extends any[]>(
  request: (...args: A) => T | Promise<TaskIdResponse>,
  args: A,
  pollingIntervalMs: number = DEFAULT_POLLING_INTERVAL,
  shouldRequestResourceAgainAfterTaskFinishes: boolean = true,
  fallbackFailureMessage?: string,
  notifyHasTask?: () => void,
  options?: Options,
  maxTimeoutMs: number = 60000
): Promise<any> => {
  return new Promise<T>(async (resolve, reject) => {
    let timeoutId: NodeJS.Timeout | null = null;

    try {
      const res: any = await request(...args);
      let task = res.data;
      if (!isTaskResponse(task)) {
        return resolve(res.data);
      }

      notifyHasTask?.();

      const taskIds: string[] = [];
      const finishedTasks: any[] = [];

      if (isTaskResponse1(task)) taskIds.push(task.task_id);
      else taskIds.push(task.task);

      // Set up timeout
      timeoutId = setTimeout(() => {
        reject(new Error(`Task polling timed out after ${maxTimeoutMs / 1000} seconds`));
      }, maxTimeoutMs);

      while (true) {
        /* Once the array is empty, all tasks have finished. */
        if (taskIds.length === 0) {
          // Clear timeout on success
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          if (someTaskInfoHasErrorType(finishedTasks[0])) {
            return reject(
              fallbackFailureMessage ??
                /* THIS NEEDS SOME TYPE-SAFETY WORK */
                //@ts-ignore
                finishedTasks[0].result.tasks_info
            );
          }

          return resolve(
            (finishedTasks[0] as SuccessfullTaskResponse<T>).result
          );
        }

        const pollingPromises = options?.useV1Polling
          ? taskIds.map((id) => getTaskProgressV1(id, options?.tokenOverride))
          : [] // TODO totally remove v3 option..

        Promise.allSettled(pollingPromises).then((pollingResponses) =>
          pollingResponses.forEach(async (response) => {
            if (response.status === "fulfilled") {
              const [taskKey] = Object.keys(response.value.data.task_results);
              const taskResult = response.value.data.task_results[taskKey];

              if (isFailureTaskResult(taskResult)) {
                // Clear timeout on error
                if (timeoutId) {
                  clearTimeout(timeoutId);
                }
                if (typeof taskResult.result === "string") {
                  return reject(new Error(taskResult.result));
                }

                if (typeof taskResult.result === "object") {
                  for (let key of possibleErrorMessageKeys) {
                    if (key in taskResult.result) {
                      return reject(new Error(taskResult.result[key]));
                    }
                  }
                }

                return reject(
                  new Error(fallbackFailureMessage ?? "Task Failed")
                );
              }

              if (hasTaskFinished(taskResult)) {
                const index = taskIds.indexOf(taskKey);
                if (index !== -1) taskIds.splice(index, 1);
                /*
                  it's important to check first for next task
                  because both NextTaskResponse and SuccessfullTaskResponse
                  have the same state
                */
                const nextTaskIds = getNextTaskIds(taskResult);

                if (nextTaskIds) taskIds.push(...nextTaskIds);

                if (hasOperationFailed(taskResult)) {
                  return reject(
                    new Error(
                      fallbackFailureMessage ?? taskResult.result.message
                    )
                  );
                } else if (shouldRequestResourceAgainAfterTaskFinishes) {
                  try {
                    const res = await pollTaskId(
                      request,
                      args,
                      pollingIntervalMs,
                      shouldRequestResourceAgainAfterTaskFinishes,
                      fallbackFailureMessage,
                      notifyHasTask
                    );
                    return resolve(res as T);
                  } catch (error) {
                    return reject(error);
                  }
                } else {
                  finishedTasks.push(taskResult);
                }
              }
            }
          })
        );

        await delay(pollingIntervalMs);
      }
    } catch (error) {
      // Clear timeout on error
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      reject(error);
    }
  });
};
